import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App.js";
import { ToastProvider } from "./components/toast.js";
import { SessionExpiredHandler } from "./components/session-expired-handler.js";
import { ApiCallError } from "./api/client.js";
import "./styles/global.css";
import "./styles/public.css";

/**
 * Shared toast dispatch for the global MutationCache.
 * Updated at runtime by <ToastBridge /> inside the React tree.
 */
type ToastFn = (type: "error" | "warning" | "info" | "success", msg: string) => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _toastFn: ToastFn = () => {}; // no-op until bridge mounts
export function setGlobalToast(fn: ToastFn) { _toastFn = fn; }

const mutationCache = new MutationCache({
  onError(error) {
    // Skip 401 — handled by SessionExpiredHandler
    if (error instanceof ApiCallError && error.status === 401) return;
    // Skip 404 — usually expected "not found" from optimistic checks
    if (error instanceof ApiCallError && error.status === 404) return;

    const msg =
      error instanceof ApiCallError
        ? error.message
        : error instanceof Error
          ? error.message
          : "An unexpected error occurred. Please try again.";

    _toastFn("error", msg);
  },
});

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 60_000,
    },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <SessionExpiredHandler />
          <App />
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
